/**
 * Melden — der Einstieg (Etappe 1, F2/F4–F8; Umbau #108, Hans 28.09.2026).
 *
 * Melden heißt Screenshot machen: Anwendung öffnen, Tab verbinden, Screenshot.
 * Mit dem ersten Screenshot (Tab-Aufnahme oder Bilddatei) legt diese Seite
 * die Meldung an, lädt das Bild hoch und wechselt in die Meldung selbst —
 * die Detailansicht im Erfassungsmodus (MeldungDetailPage). Ein Leerformular
 * gibt es nicht mehr, ebenso wenig die frühere Seitenleiste „Neue Meldung".
 *
 * Erfassungsquellen (Adapter-Schnitt, SCHNITTLISTE §5), je Anwendung:
 *  - „web-tab"    (WebTabSource):    Web-Anwendung im eigenen Browser-Tab
 *    (Standard, Issue #61) — Screenshot per Tab-Freigabe mit Live-Vorschau.
 *  - „ninox"      (NinoxTabSource):  Ninox im Fremd-Tab (Deep-Link, Live/DEV).
 *  - „web-iframe" (WebIframeSource): Alt-Modus same-origin-iframe (nur noch
 *    per VITE_WEB_EINBETTUNG=iframe) — behält seinen „Melden"-Knopf, der
 *    Screenshot führt ebenfalls in die Meldung.
 */

import { useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { ChevronRight } from "lucide-react"
import { Navigate, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { erfassungsquelle, istNinoxAnwendung } from "@/components/AppSwitcher"
import { NinoxTabSource } from "@/components/erfassung/quellen/NinoxTabSource"
import { WebIframeSource } from "@/components/erfassung/quellen/WebIframeSource"
import { WebTabSource } from "@/components/erfassung/quellen/WebTabSource"
import { dateiAlsBlob } from "@/lib/screenshot"
import { api, apiError } from "@/services/api"
import type { Application, ReportKontext } from "@/types"

export function ErfassungPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const entwurfParam = searchParams.get("entwurf")
  // Zielbild #36: Der Aufrufer (Käfer-Verweis der Anwendung) weiß, aus
  // welcher Stufe er kommt, und übergibt sie — der Melder muss die Umgebung
  // nicht mehr selbst wissen. `prod`/`live` ⇒ live, alles andere ⇒ dev.
  const umgebungParam = searchParams.get("umgebung")

  const dateiInputRef = useRef<HTMLInputElement>(null)
  // Route-Auslesung der web-Quelle (WebIframeSource registriert den Leser)
  const routeLeserRef = useRef<(() => string | null) | null>(null)

  const [appId, setAppId] = useState<number | null>(null)
  // Nur der iframe-Alt-Modus legt den Entwurf noch per „Melden"-Knopf an
  const [entwurfId, setEntwurfId] = useState<number | null>(null)
  const [umgebung, setUmgebung] = useState<"live" | "dev">(
    umgebungParam === "prod" || umgebungParam === "live" ? "live" : "dev"
  )

  const { data: anwendungen } = useQuery({
    queryKey: ["applications"],
    queryFn: api.applications.list,
  })
  const anwendung: Application | undefined = useMemo(() => {
    if (appId !== null) return anwendungen?.find((a) => a.id === appId)
    if (!anwendungen || anwendungen.length === 0) return undefined
    if (anwendungen.length === 1) return anwendungen[0]
    // Mehrere Anwendungen: die zur aufgerufenen Domain passende wählen
    // (Alt-Modus same-origin). Ninox-Anwendungen matchen nie → explizite
    // Auswahl. KEIN stiller Fallback auf die erste App: ein falsch
    // zugeordneter Report erzeugt sein Issue im falschen Repo.
    return anwendungen.find((a) => {
      try {
        if (new URL(a.url).origin === window.location.origin) return true
        return !!a.urlLive && new URL(a.urlLive).origin === window.location.origin
      } catch {
        return false
      }
    })
  }, [anwendungen, appId])

  const quelle = anwendung !== undefined ? erfassungsquelle(anwendung) : null
  const ninoxQuelle = quelle === "ninox"

  // Bereitschafts-Gate: Ninox-Anwendung erst meldbar, wenn nxtools das Team
  // analysiert hat (Struktur für den KI-Dialog). null/true = kein Gate.
  const ninoxStatus = useQuery({
    queryKey: ["applications", anwendung?.id, "ninox-status"],
    queryFn: () => api.applications.ninoxStatus(anwendung!.id),
    enabled: ninoxQuelle && anwendung !== undefined,
    refetchInterval: (q) => (q.state.data && q.state.data.bereit === false ? 30_000 : false),
  })
  const meldenGesperrtGrund =
    ninoxQuelle && ninoxStatus.data?.bereit === false
      ? "Die Anwendung wird vorbereitet — die Struktur-Analyse läuft. In wenigen Minuten geht es los."
      : undefined

  /** F8 (ninox): Kontext aus der App-Zuordnung. Die betroffene Tabelle
      ermittelt die KI aus Dialog + Screenshots gegen die DEV-Struktur. */
  const kontextNinox = (app: Application): ReportKontext => ({
    umgebung,
    browser: navigator.userAgent,
    zeitpunkt: new Date().toISOString(),
    applicationUrl: app.url,
  })

  /** Aktive URL der web-Quelle: live nur, wenn `urlLive` gepflegt ist. */
  const webUrl = (app: Application): string =>
    umgebung === "live" && app.urlLive ? app.urlLive : app.url

  /** F8 (web): Kontext-Metadaten — Route aus dem iframe (falls Alt-Modus),
      Version aus dem Health-Endpoint. */
  const kontextWeb = async (app: Application): Promise<ReportKontext> => {
    const aktiveUrl = webUrl(app)
    const route = routeLeserRef.current?.() ?? null
    let appVersion: string | null = null
    try {
      const res = await fetch(`${aktiveUrl}/api/v1/health`, { signal: AbortSignal.timeout(2000) })
      appVersion = (await res.json()).version ?? null
    } catch {
      appVersion = null
    }
    return {
      route,
      appVersion,
      umgebung,
      applicationUrl: aktiveUrl,
      browser: navigator.userAgent,
      zeitpunkt: new Date().toISOString(),
    }
  }

  const kontextErmitteln = (app: Application): Promise<ReportKontext> =>
    istNinoxAnwendung(app) ? Promise.resolve(kontextNinox(app)) : kontextWeb(app)

  // Kern von #108: Screenshot → Meldung anlegen (falls noch keine) → Bild
  // hochladen → in die Meldung wechseln.
  const meldungBeginnen = useMutation({
    mutationFn: async (bild: Blob) => {
      if (!anwendung) throw new Error("Keine Anwendung")
      let id = entwurfId
      if (id === null) {
        const kontext = await kontextErmitteln(anwendung)
        id = (await api.reports.create({ applicationId: anwendung.id, kontext })).id
        setEntwurfId(id)
      }
      await api.reports.screenshots.upload(id, bild, [])
      return id
    },
    onSuccess: (id) => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] })
      navigate(`/meldungen/${id}`)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  // iframe-Alt-Modus: „Melden" legt den Entwurf an, der Screenshot folgt dort
  const beginnen = useMutation({
    mutationFn: async () => {
      if (!anwendung) throw new Error("Keine Anwendung")
      const kontext = await kontextErmitteln(anwendung)
      return api.reports.create({ applicationId: anwendung.id, kontext })
    },
    onSuccess: (report) => setEntwurfId(report.id),
    onError: (e) => toast.error(apiError(e)),
  })

  // Alte Links (?entwurf=<id>) führen direkt in die Meldung
  if (entwurfParam) return <Navigate to={`/meldungen/${entwurfParam}`} replace />

  if (!anwendungen) return <p className="text-muted-foreground">Lade Anwendungen …</p>
  if (anwendungen.length === 0) {
    return (
      <p className="rounded-md bg-warning/10 px-4 py-3 text-warning">
        Keine aktive Anwendung konfiguriert — Anlage durch netz-fabrik (F3).
      </p>
    )
  }
  if (!anwendung) {
    return (
      <div className="mx-auto max-w-lg pt-10">
        <h1 className="mb-1.5 text-[22px] font-bold">Welche Anwendung möchtest du testen?</h1>
        <p className="mb-5 text-sm text-muted-foreground">
          Diese Adresse ist keiner Anwendung eindeutig zugeordnet — bitte wähle aus, damit
          die Meldung im richtigen Projekt landet.
        </p>
        <div className="flex flex-col gap-2.5">
          {anwendungen.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setAppId(a.id)}
              className="flex items-center justify-between rounded-xl border bg-card px-5 py-4 text-left shadow-card transition-colors hover:border-primary/40"
            >
              <span>
                <span className="block text-[15px] font-semibold">{a.name}</span>
                <span className="mt-0.5 block text-[13px] text-muted-foreground">{a.url}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
            </button>
          ))}
        </div>
      </div>
    )
  }

  const bildAufgenommen = (bild: Blob) => meldungBeginnen.mutate(bild)
  const beschaeftigt = meldungBeginnen.isPending

  return (
    <div className="flex gap-4" style={{ height: "calc(100vh - 8rem)" }}>
      {ninoxQuelle ? (
        <NinoxTabSource
          key={anwendung.id}
          anwendung={anwendung}
          meldenGesperrtGrund={meldenGesperrtGrund}
          beschaeftigt={beschaeftigt}
          onBild={bildAufgenommen}
          onDateiWaehlen={() => dateiInputRef.current?.click()}
          onUmgebungChange={setUmgebung}
        />
      ) : quelle === "web-tab" ? (
        <WebTabSource
          key={anwendung.id}
          anwendung={anwendung}
          beschaeftigt={beschaeftigt}
          onBild={bildAufgenommen}
          onDateiWaehlen={() => dateiInputRef.current?.click()}
          onUmgebungChange={setUmgebung}
        />
      ) : (
        <WebIframeSource
          key={anwendung.id}
          anwendung={anwendung}
          url={webUrl(anwendung)}
          umgebung={umgebung}
          onUmgebungChange={setUmgebung}
          entwurfId={entwurfId}
          meldenStarten={() => beginnen.mutate()}
          meldenLaeuft={beginnen.isPending}
          onBild={bildAufgenommen}
          onDateiWaehlen={() => dateiInputRef.current?.click()}
          routeLeserRef={routeLeserRef}
        />
      )}

      {/* Ersatzweg (alle Quellen): Bilddatei statt Tab-Aufnahme */}
      <input
        ref={dateiInputRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void dateiAlsBlob(file).then(bildAufgenommen)
          e.target.value = ""
        }}
      />
    </div>
  )
}
